/**
 * @file psychiatricSupport.js
 * @version 1.0.0
 * @date October 10, 2026
 *
 * @description
 * Psychiatric (behavioural health) support level, a Tab 3 planning assumption (Dr. Tatar Radu MD, 2026-10-10):
 * "some psychiatric support should help lower the evacuation or the number of days a casualty needs treatment".
 * CONSTANTS ONLY (APP_RULES 2.1); applied in calculationEngine/modules/dnbiModule.js (RTD within 72 h of combat stress
 * and psychiatric casualties) and in the DNBI bed-days of casualtyCalculator.
 *
 * REFERENCE LEVEL: STANDARD = the behavioural health support of a US brigade combat team in Iraq (a behavioural
 * health team with a psychiatrist or psychologist available), the unit of Belmont et al., Military Medicine 2010, where
 * 88% of psychiatric casualties were treated in theatre and returned to duty and 11% were evacuated. STANDARD has
 * multiplier 1.0, so the existing mentalHealth.js rates stay as they are for the default.
 * NONE and ENHANCED are the assistant's PLACEHOLDER multipliers around that reference (BICEPS: 90%+ return to duty
 * when applied correctly, mentalHealth.js BICEPS_PRINCIPLES), to be confirmed by Dr. Tatar.
 */

export const PSYCHIATRIC_SUPPORT_STATUS = Object.freeze({
  REFERENCE: 'REFERENCE',
  PLACEHOLDER: 'PLACEHOLDER'
});

/**
 * rtd72Multiplier: multiplies the share of combat stress casualties that return to duty within 72 h (capped at
 *   rtd72Cap). More return to duty means fewer evacuated.
 * treatmentDaysMultiplier: multiplies the days a behavioural health casualty needs treatment (bed or restoration days).
 */
export const PSYCHIATRIC_SUPPORT_LEVELS = {
  NONE: {
    label: 'None',
    description: 'No dedicated behavioural health capability: unit leaders and medics only; BICEPS rarely applied.',
    rtd72Multiplier: 0.60,
    treatmentDaysMultiplier: 1.5,
    status: PSYCHIATRIC_SUPPORT_STATUS.PLACEHOLDER
  },
  STANDARD: {
    label: 'Standard',
    description: 'Brigade behavioural health team (technician, social worker, psychiatrist or psychologist on call): the reference of the sourced rates.',
    rtd72Multiplier: 1.0,
    treatmentDaysMultiplier: 1.0,
    status: PSYCHIATRIC_SUPPORT_STATUS.REFERENCE
  },
  ENHANCED: {
    label: 'Enhanced',
    description: 'Combat stress control detachment with restoration beds forward and BICEPS applied consistently.',
    rtd72Multiplier: 1.30,
    treatmentDaysMultiplier: 0.70,
    status: PSYCHIATRIC_SUPPORT_STATUS.PLACEHOLDER
  }
};

/** Upper limit of the share returning to duty within 72 h (BICEPS: 90%+ when applied correctly). */
export const PSYCHIATRIC_RTD72_CAP = 0.95;

export const DEFAULT_PSYCHIATRIC_SUPPORT = 'STANDARD';

/**
 * Support level of a scenario (medical_configuration.psychiatric_support). Absent means the planner has not chosen:
 * the reference level applies and the caller lists the assumption. An unknown value throws.
 * @returns {{level:string, defaulted:boolean}}
 */
export const resolvePsychiatricSupport = (medicalConfig) => {
  const value = medicalConfig?.psychiatric_support;
  if (value === undefined || value === null || value === '') return { level: DEFAULT_PSYCHIATRIC_SUPPORT, defaulted: true };
  if (!Object.prototype.hasOwnProperty.call(PSYCHIATRIC_SUPPORT_LEVELS, value)) {
    throw new Error(`resolvePsychiatricSupport: unknown psychiatric_support '${value}' (valid: ${Object.keys(PSYCHIATRIC_SUPPORT_LEVELS).join(', ')}). Path: medical_configuration.psychiatric_support (set in Tab 3)`);
  }
  return { level: value, defaulted: false };
};

export const listPsychiatricSupportPlaceholders = () =>
  Object.entries(PSYCHIATRIC_SUPPORT_LEVELS)
    .filter(([, v]) => v.status === PSYCHIATRIC_SUPPORT_STATUS.PLACEHOLDER)
    .map(([k]) => ({ id: `psychiatric_support_${k.toLowerCase()}`, basis: 'Multipliers proposed 2026-10-10; STANDARD is the sourced reference (Belmont 2010).' }));
